import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { useGetCustomService } from '../../../../hooks/rest/useGetCustomService';
import { useGenericAPIMutation } from '../../../../hooks/mutations/useGenericAPIMutation';
import { memberProfileSelector } from '../../../../redux-store/auth/authSelectors';
import { CURRENT_PAGE, PAGE_SIZE } from '../../../../constants/constants';

const useClientNotesData = (contactId) => {
    const queryClient = useQueryClient();
    const profile = useSelector(memberProfileSelector);
    const [currentPage, setCurrentPage] = useState(CURRENT_PAGE);
    const [error, setError] = useState<any>(null);

    const queryPayload = { contactId, pageSize: PAGE_SIZE, pageNumber: currentPage };

    const {
        data: response, error: fetchError, isFetching, refetch,
    } = useGetCustomService({
        service: 'customService',
        api: 'getClientNotes',
        payload: queryPayload,
        enabled: !!contactId,
    });

    const data = response?.data?.notes ?? null;
    const totalNumber = response?.data?.total ?? null;

    const { mutateAsync, isPending: saveInProgress } = useGenericAPIMutation();

    const onPageChange = useCallback(async (paging) => {
        setCurrentPage(paging?.currentPage);
    }, []);

    const getClientNotes = useCallback(async () => {
        setError(null);
        await refetch();
    }, [refetch, currentPage]);

    const saveClientNotes = useCallback(async (note) => {
        try {
            await mutateAsync({
                service: 'customService',
                API: 'saveClientNote',
                payload: { contactId, message: note, createdBy: profile?.Id },
            });

            const newNote = {
                contactId,
                message: note,
                createdBy: profile?.Id,
                createdById: profile?.Id,
                createdByName: `${profile?.FirstName} ${profile?.LastName}`,
                createdDate: new Date(),
            };

            queryClient.setQueryData(
                ['getData_getClientNotes', queryPayload],
                (prev: any) => {
                    if (!prev?.data) {
                        return prev;
                    }

                    return {
                        ...prev,
                        data: {
                            ...prev.data,
                            notes: [newNote, ...(prev.data.notes ?? [])],
                            total: (prev.data.total ?? 0) + 1,
                        },
                    };
                },
            );
        } catch (err) {
            setError(err);
            throw err;
        }
    }, [contactId, profile, mutateAsync, queryClient, currentPage]);

    return {
        getClientNotes,
        saveClientNotes,
        loading: isFetching,
        saveInProgress,
        error: error || fetchError,
        data,
        setError,
        totalNumber,
        onPageChange,
        currentPage,
    };
};

export default useClientNotesData;
